import React from 'react';

import { PREFERRED_LANGUAGE_OPTIONS } from '@/services/types/matter-client-types';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../ui/card';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import { CONTACT_METHODS } from '../constants';
// Note: Textarea import removed - ReferralSource now uses a dropdown
import { IntakeFormData } from '../form/types';

interface PersonalInfoSectionProps {
  form: IntakeFormData;
  onFieldChange: (
    section: keyof IntakeFormData,
    field: string,
    value: any,
  ) => void;
}

const PersonalInfoSection: React.FC<PersonalInfoSectionProps> = ({
  form,
  onFieldChange,
}) => {
  const handleInputChange = (field: string, value: string) => {
    onFieldChange('personalInfo', field, value);
  };

  return (
    <div className="space-y-3">
      {/* Personal Information */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <svg
              className="w-4 h-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
              />
            </svg>
            <CardTitle className="text-base">Personal Information</CardTitle>
          </div>
          <CardDescription className="text-xs text-gray-500 leading-tight">
            Basic contact and identification information
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="fullName" className="text-sm font-medium">
                Full Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="fullName"
                value={form.personalInfo.fullName}
                onChange={(e) => handleInputChange('fullName', e.target.value)}
                placeholder="Legal name as it appears on ID"
                aria-label="Full name"
                tabIndex={0}
                className="text-sm"
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="dob" className="text-sm font-medium">
                Date of Birth
              </Label>
              <Input
                id="dob"
                type="date"
                value={form.personalInfo.dob}
                onChange={(e) => handleInputChange('dob', e.target.value)}
                aria-label="Date of birth"
                tabIndex={0}
                className="text-sm"
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="ssn" className="text-sm font-medium">
                SSN (Last 4 or Full)
              </Label>
              <Input
                id="ssn"
                value={form.personalInfo.ssnLast4}
                onChange={(e) => handleInputChange('ssnLast4', e.target.value)}
                placeholder="1234 or 123456789"
                aria-label="SSN"
                tabIndex={0}
                className="text-sm"
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="phone" className="text-sm font-medium">
                Phone Number <span className="text-red-500">*</span>
              </Label>
              <Input
                id="phone"
                type="tel"
                value={form.personalInfo.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                placeholder="(555) 123-4567"
                aria-label="Phone number"
                tabIndex={0}
                className="text-sm"
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="email" className="text-sm font-medium">
                Email Address
              </Label>
              <Input
                id="email"
                type="email"
                value={form.personalInfo.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="your.email@example.com"
                aria-label="Email address"
                tabIndex={0}
                className="text-sm"
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="address" className="text-sm font-medium">
                Address
              </Label>
              <Input
                id="address"
                value={form.personalInfo.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                placeholder="Current residential address"
                aria-label="Address"
                tabIndex={0}
                className="text-sm"
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="preferredContact" className="text-sm font-medium">
                Preferred Contact Method
              </Label>
              <Select
                value={form.personalInfo.preferredContact}
                onValueChange={(value) =>
                  handleInputChange('preferredContact', value)
                }
              >
                <SelectTrigger
                  id="preferredContact"
                  tabIndex={0}
                  className="text-sm"
                >
                  <SelectValue placeholder="Select preferred method" />
                </SelectTrigger>
                <SelectContent>
                  {CONTACT_METHODS.map((method) => (
                    <SelectItem key={method} value={method} className="text-sm">
                      {method}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label htmlFor="bestTimeToReach" className="text-sm font-medium">
                Best Time to Reach
              </Label>
              <Input
                id="bestTimeToReach"
                value={form.personalInfo.bestTimeToReach}
                onChange={(e) =>
                  handleInputChange('bestTimeToReach', e.target.value)
                }
                placeholder="e.g., Weekdays 9-5, Evenings only"
                aria-label="Best time to reach"
                tabIndex={0}
                className="text-sm"
              />
            </div>

            <div className="space-y-1">
              <Label
                htmlFor="preferredLanguage"
                className="text-sm font-medium"
              >
                Preferred Language
              </Label>
              <Select
                value={form.personalInfo.preferredLanguage}
                onValueChange={(value) =>
                  handleInputChange('preferredLanguage', value)
                }
              >
                <SelectTrigger
                  id="preferredLanguage"
                  tabIndex={0}
                  className="text-sm"
                >
                  <SelectValue placeholder="Select preferred language" />
                </SelectTrigger>
                <SelectContent>
                  {PREFERRED_LANGUAGE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value} className="text-sm">
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Emergency Contact */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Emergency Contact</CardTitle>
          <CardDescription className="text-xs text-gray-500 leading-tight">
            Person to contact in case of emergency
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label
                htmlFor="emergencyContactName"
                className="text-sm font-medium"
              >
                Emergency Contact Name
              </Label>
              <Input
                id="emergencyContactName"
                value={form.personalInfo.emergencyContact.name}
                onChange={(e) =>
                  handleInputChange('emergencyContact.name', e.target.value)
                }
                placeholder="Full name of emergency contact"
                aria-label="Emergency contact name"
                tabIndex={0}
                className="text-sm"
              />
            </div>

            <div className="space-y-1">
              <Label
                htmlFor="emergencyContactPhone"
                className="text-sm font-medium"
              >
                Emergency Contact Phone
              </Label>
              <Input
                id="emergencyContactPhone"
                type="tel"
                value={form.personalInfo.emergencyContact.phone}
                onChange={(e) =>
                  handleInputChange('emergencyContact.phone', e.target.value)
                }
                placeholder="(555) 123-4567"
                aria-label="Emergency contact phone"
                tabIndex={0}
                className="text-sm"
              />
            </div>

            <div className="space-y-1">
              <Label
                htmlFor="emergencyContactRelationship"
                className="text-sm font-medium"
              >
                Relationship
              </Label>
              <Input
                id="emergencyContactRelationship"
                value={form.personalInfo.emergencyContact.relationship}
                onChange={(e) =>
                  handleInputChange(
                    'emergencyContact.relationship',
                    e.target.value,
                  )
                }
                placeholder="e.g., Spouse, Parent, Friend"
                aria-label="Emergency contact relationship"
                tabIndex={0}
                className="text-sm"
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PersonalInfoSection;
